import { STROKE, NUMERIC, STATE, TEXT, type StateName } from '../theme/tokens';

// A pure-SVG donut. No chart library — `package.json` carries none, and one
// would be a dependency the console does not otherwise need for five arcs.
//
// Segment colours are `glyph` values, which is correct: an arc is a GRAPHIC, and
// the legend's LABEL is ordinary body text on the card, not text on a tint. The
// figure in the middle is ink, never a state colour.

export interface DonutSegment {
  label: string;
  value: number;
  state: StateName;
}

export interface DonutProps {
  segments: DonutSegment[];
  /** The big number. Defaults to the sum. */
  centerValue?: string | number;
  centerLabel?: string;
  size?: number;
  thickness?: number;
  /** Legend rows carry the count and the share. */
  showLegend?: boolean;
}

export default function Donut({
  segments,
  centerValue,
  centerLabel,
  size = 148,
  thickness = 16,
  showLegend = true,
}: DonutProps) {
  const total = segments.reduce((sum, segment) => sum + Math.max(0, segment.value), 0);
  const radius = (size - thickness) / 2;
  const circumference = 2 * Math.PI * radius;

  // A donut over nothing is the case that produces a full ring of the first
  // colour if you divide by the total without checking. It degrades to an empty
  // track and a zero, which is the honest rendering.
  let offset = 0;
  const arcs =
    total > 0
      ? segments
          .filter((segment) => segment.value > 0)
          .map((segment) => {
            const fraction = segment.value / total;
            const arc = {
              key: segment.label,
              color: STATE[segment.state].glyph,
              dash: `${fraction * circumference} ${circumference}`,
              rotation: offset * 360,
            };
            offset += fraction;
            return arc;
          })
      : [];

  return (
    <div className="flex flex-wrap items-center gap-6">
      <svg
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        role="img"
        aria-label={
          total > 0
            ? segments.map((s) => `${s.label}: ${s.value}`).join(', ')
            : 'No devices to chart'
        }
      >
        <g transform={`translate(${size / 2}, ${size / 2})`}>
          <circle
            r={radius}
            fill="none"
            stroke={STROKE.base}
            strokeWidth={thickness}
            aria-hidden="true"
          />
          {arcs.map((arc) => (
            <circle
              key={arc.key}
              r={radius}
              fill="none"
              stroke={arc.color}
              strokeWidth={thickness}
              strokeDasharray={arc.dash}
              // -90 puts the first segment at twelve o'clock; the per-arc
              // rotation stacks them clockwise from there.
              transform={`rotate(${-90 + arc.rotation})`}
              strokeLinecap="butt"
            />
          ))}
          <text
            textAnchor="middle"
            dominantBaseline="central"
            dy={centerLabel ? '-0.35em' : '0'}
            style={{ fontSize: 22, fontWeight: 600, fill: TEXT.primary, ...NUMERIC }}
          >
            {centerValue ?? total}
          </text>
          {centerLabel && (
            <text
              textAnchor="middle"
              dominantBaseline="central"
              dy="1.15em"
              style={{ fontSize: 10, fill: TEXT.secondary }}
            >
              {centerLabel}
            </text>
          )}
        </g>
      </svg>

      {showLegend && (
        <ul className="m-0 min-w-[180px] flex-1 list-none p-0">
          {segments.map((segment) => (
            <li
              key={segment.label}
              className="flex items-center justify-between gap-3 py-1 text-[12px]"
            >
              <span className="flex min-w-0 items-center gap-2">
                <span
                  aria-hidden="true"
                  className="h-2 w-2 shrink-0 rounded-full"
                  style={{ backgroundColor: STATE[segment.state].glyph }}
                />
                <span className="truncate" style={{ color: TEXT.primary }}>
                  {segment.label}
                </span>
              </span>
              <span className="flex shrink-0 items-baseline gap-2">
                <span style={{ color: TEXT.primary, fontWeight: 500, ...NUMERIC }}>
                  {segment.value.toLocaleString()}
                </span>
                <span style={{ color: TEXT.secondary, ...NUMERIC }}>
                  {total > 0 ? `${Math.round((segment.value / total) * 100)}%` : '—'}
                </span>
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
